import { useState, useEffect } from 'react';
import { Gift, Copy, Check, Users, DollarSign, Loader2, Wallet, AlertCircle, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Modal from './ui/Modal';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ReferralModal = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    if (isOpen && token) {
      fetchReferralData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token]);

  const fetchReferralData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE}/api/auth/referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(response.data);
    } catch (err) {
      console.error('Failed to fetch referral data:', err);
      setError('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!referralData?.referral_link) return;
    
    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleWithdraw = async () => {
    if (!referralData?.canWithdraw) return;
    
    setWithdrawing(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/auth/referrals/withdraw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Withdrawal request submitted!');
      // Refresh data to show updated balance
      await fetchReferralData();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to process withdrawal';
      toast.error(message);
    } finally {
      setWithdrawing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const modalIcon = <Gift className="w-5 h-5 text-white" />;

  // Calculate balance
  const currentBalance = referralData 
    ? (referralData.earnedAmount || 0) - (referralData.paidOut || 0)
    : 0;
  const minimumPayout = referralData?.minimumPayout || 100;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Refer & Earn"
      icon={modalIcon}
      maxWidth="max-w-lg"
      testId="referral-modal"
    >
      {/* Content */}
      <div className="p-6 space-y-5">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7C5CFF' }} />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p style={{ color: '#F87171' }}>{error}</p>
            <button 
              onClick={fetchReferralData}
              className="mt-3 text-sm underline"
              style={{ color: '#7C5CFF' }}
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {/* How it works */}
            <div 
              className="p-4 rounded-lg flex items-start gap-3"
              style={{ 
                background: 'rgba(124, 92, 255, 0.1)',
                border: '1px solid rgba(124, 92, 255, 0.2)'
              }}
            >
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#7C5CFF' }} />
              <div className="text-sm" style={{ color: '#A1A1AA' }}>
                <p className="font-medium mb-1" style={{ color: '#FAFAFA' }}>How it works</p>
                <p>Share your link. When someone signs up and upgrades to Pro, you earn <strong style={{ color: '#4ade80' }}>$5</strong>. Withdraw once you reach ${minimumPayout}.</p>
              </div>
            </div>

            {/* Referral Link */}
            <div>
              <label className="text-sm font-medium" style={{ color: '#A1A1AA' }}>
                Your Referral Link
              </label>
              <div 
                className="mt-1.5 flex items-center gap-2"
              >
                <div 
                  className="flex-1 px-4 py-3 rounded-lg text-sm truncate"
                  style={{ 
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#FAFAFA'
                  }}
                  data-testid="referral-link"
                >
                  {referralData?.referral_link || 'Loading...'}
                </div>
                <button
                  onClick={handleCopyLink}
                  className="px-4 py-3 rounded-lg transition-all flex items-center gap-2 shrink-0"
                  style={{
                    background: copied 
                      ? 'rgba(34, 197, 94, 0.2)' 
                      : 'linear-gradient(135deg, #7C5CFF 0%, #6B4FE0 100%)',
                    color: copied ? '#22C55E' : 'white'
                  }}
                  data-testid="copy-referral-link"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Stats - Aggregated only, no PII */}
            <div className="grid grid-cols-2 gap-4">
              <div 
                className="p-4 rounded-lg"
                style={{ 
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4" style={{ color: '#7C5CFF' }} />
                  <span className="text-xs font-medium" style={{ color: '#71717A' }}>
                    Successful Referrals
                  </span>
                </div>
                <p className="text-2xl font-bold" style={{ color: '#FAFAFA' }} data-testid="referral-count">
                  {referralData?.referralCount || 0}
                </p>
              </div>
              <div 
                className="p-4 rounded-lg"
                style={{ 
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4" style={{ color: '#22C55E' }} />
                  <span className="text-xs font-medium" style={{ color: '#71717A' }}>
                    Available Balance
                  </span>
                </div>
                <p className="text-2xl font-bold" style={{ color: '#FAFAFA' }} data-testid="referral-balance">
                  ${currentBalance.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Withdrawal Section */}
            <div 
              className="p-4 rounded-lg"
              style={{ 
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4" style={{ color: '#f59e0b' }} />
                  <span className="text-sm font-medium" style={{ color: '#FAFAFA' }}>
                    Cash Out
                  </span>
                </div>
                <span className="text-xs" style={{ color: '#71717A' }}>
                  Min. ${minimumPayout}
                </span>
              </div>
              
              {/* Progress to minimum */}
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-1" style={{ color: '#71717A' }}>
                  <span>${currentBalance.toFixed(2)}</span>
                  <span>${minimumPayout}</span>
                </div>
                <div 
                  className="h-2 rounded-full overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.1)' }}
                >
                  <div 
                    className="h-full rounded-full transition-all duration-300"
                    style={{ 
                      width: `${Math.min((currentBalance / minimumPayout) * 100, 100)}%`,
                      background: referralData?.canWithdraw 
                        ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'
                        : 'linear-gradient(135deg, #7C5CFF 0%, #6B4FE0 100%)'
                    }}
                  />
                </div>
              </div>

              {referralData?.canWithdraw ? (
                <button
                  onClick={handleWithdraw}
                  disabled={withdrawing}
                  className="w-full py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                  style={{
                    background: withdrawing 
                      ? 'rgba(34, 197, 94, 0.3)' 
                      : 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                    color: 'white'
                  }}
                  data-testid="withdraw-btn"
                >
                  {withdrawing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Wallet className="w-4 h-4" />
                      Withdraw ${currentBalance.toFixed(2)}
                    </>
                  )}
                </button>
              ) : (
                <div 
                  className="text-center py-2 text-sm rounded-lg"
                  style={{ 
                    background: 'rgba(255,255,255,0.03)',
                    color: '#71717A' 
                  }}
                >
                  ${(minimumPayout - currentBalance).toFixed(2)} more to withdraw
                </div>
              )}
            </div>

            {/* Payout History */}
            {referralData?.payoutHistory && referralData.payoutHistory.length > 0 && (
              <div>
                <label className="text-sm font-medium" style={{ color: '#A1A1AA' }}>
                  Payout History
                </label>
                <div 
                  className="mt-1.5 rounded-lg overflow-hidden"
                  style={{ 
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                    {referralData.payoutHistory.map((payout, idx) => (
                      <div 
                        key={idx} 
                        className="px-4 py-3 flex items-center justify-between"
                      >
                        <span className="text-sm" style={{ color: '#71717A' }}>
                          {formatDate(payout.date)}
                        </span>
                        <span 
                          className="text-sm font-medium"
                          style={{ color: '#4ade80' }}
                        >
                          +${payout.amount.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div 
        className="px-6 py-4 border-t shrink-0"
        style={{ borderColor: 'rgba(255,255,255,0.1)' }}
      >
        <button
          onClick={onClose}
          className="w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{
            background: 'rgba(255,255,255,0.05)',
            color: '#A1A1AA',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default ReferralModal;
